/**
 * TechPhotoReport — Paso 0 opcional del flujo del técnico.
 * Captura fotográfica MIP por zonas durante el servicio.
 * Fotos comprimidas en navegador antes de subir.
 */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    ArrowLeft, Camera, Trash2, Mic, MicOff, CheckCircle2,
} from "lucide-react";

// ─────────────────────── Constants ──────────────────────────────────────────

const ZONES = [
    { key: "verde",    label: "Zona Verde",    color: "bg-green-100 text-green-800 border-green-300",  dot: "bg-green-500" },
    { key: "amarilla", label: "Zona Amarilla", color: "bg-yellow-100 text-yellow-800 border-yellow-300", dot: "bg-yellow-500" },
    { key: "roja",     label: "Zona Roja",     color: "bg-red-100 text-red-800 border-red-300",         dot: "bg-red-500" },
];

const AREAS = {
    verde: [
        "Perímetro exterior",
        "Jardines / áreas verdes",
        "Estacionamiento",
        "Contenedores de basura",
        "Zona de carga / descarga",
        "Drenajes externos",
        "Luminarias exteriores",
        "Otra",
    ],
    amarilla: [
        "Cocina / área de preparación",
        "Bodega de alimentos",
        "Cuarto de basura interior",
        "Vestuarios / casilleros",
        "Cuarto de máquinas",
        "Área con humedad / calor",
        "Otra",
    ],
    roja: [
        "Salón / área de atención",
        "Baños de clientes",
        "Pasillos de exhibición",
        "Recepción / área administrativa",
        "Comedor",
        "Otra",
    ],
};

const MAX_PHOTOS = 10;

// ─────────────────────── Image compression ──────────────────────────────────

function compressImage(file, maxPx = 1024, quality = 0.72) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            let { width, height } = img;
            if (width > maxPx || height > maxPx) {
                if (width > height) { height = Math.round((height * maxPx) / width); width = maxPx; }
                else { width = Math.round((width * maxPx) / height); height = maxPx; }
            }
            const canvas = document.createElement("canvas");
            canvas.width = width; canvas.height = height;
            canvas.getContext("2d").drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            }, "image/jpeg", quality);
        };
        img.onerror = reject;
        img.src = url;
    });
}

// ─────────────────────── Component ──────────────────────────────────────────

export default function TechPhotoReport() {
    const _params = useParams();
    const serviceId = _params.id || _params.serviceId;
    const navigate = useNavigate();
    const _loc = useLocation();
    const basePath = _loc.pathname.startsWith("/audit") ? "/audit" : "/tech";
    const isAudit = basePath === "/audit";
    const fileInputRef = useRef(null);

    const [activeZone, setActiveZone] = useState("verde");
    const [photos, setPhotos] = useState([]);      // {id,zone,area,custom_area,description,is_reincident,photo_url,preview}
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);

    // Per-photo edit state
    const [editId, setEditId] = useState(null);    // photo id being edited inline

    // Voice dictation
    const recognitionRef = useRef(null);
    const [listening, setListening] = useState(null); // photo id listening

    // ── Load existing photos ──────────────────────────────────────────────
    useEffect(() => {
        api.get(`/photo-reports/${serviceId}`)
            .then((r) => { if (r.data?.photos) setPhotos(r.data.photos); })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [serviceId]);

    const totalPhotos = photos.length;
    const zonePhotos = (zone) => photos.filter((p) => p.zone === zone);

    // ── Add photo ────────────────────────────────────────────────────────
    const handleFileSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (totalPhotos >= MAX_PHOTOS) { toast.error(`Límite de ${MAX_PHOTOS} fotos alcanzado`); return; }

        setUploading(true);
        try {
            const dataUri = await compressImage(file);
            const res = await api.post(`/photo-reports/${serviceId}/photos`, {
                zone: activeZone,
                area: AREAS[activeZone][0],
                description: "",
                is_reincident: false,
                data_uri: dataUri,
            });
            const newPhoto = { ...res.data.photo, preview: dataUri };
            setPhotos((prev) => [...prev, newPhoto]);
            setEditId(res.data.photo.id);
            toast.success("Foto agregada");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error al subir foto");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    // ── Delete photo ─────────────────────────────────────────────────────
    const handleDelete = async (photoId) => {
        try {
            await api.delete(`/photo-reports/${serviceId}/photos/${photoId}`);
            setPhotos((prev) => prev.filter((p) => p.id !== photoId));
            if (editId === photoId) setEditId(null);
            toast.success("Foto eliminada");
        } catch { toast.error("Error al eliminar foto"); }
    };

    // ── Update photo metadata ─────────────────────────────────────────────
    const updatePhoto = async (photoId, fields) => {
        setPhotos((prev) => prev.map((p) => p.id === photoId ? { ...p, ...fields } : p));
        try {
            await api.patch(`/photo-reports/${serviceId}/photos/${photoId}`, fields);
        } catch { toast.error("Error al guardar cambios"); }
    };

    // ── Voice dictation ───────────────────────────────────────────────────
    const toggleVoice = (photoId) => {
        if (!("webkitSpeechRecognition" in window || "SpeechRecognition" in window)) {
            toast.error("Dictado no disponible en este navegador");
            return;
        }
        if (listening === photoId) {
            recognitionRef.current?.stop();
            setListening(null);
            return;
        }
        const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
        const rec = new SR();
        rec.lang = "es-SV";
        rec.continuous = false;
        rec.interimResults = false;
        rec.onresult = (ev) => {
            const transcript = ev.results[0][0].transcript;
            const current = photos.find((p) => p.id === photoId)?.description || "";
            const newDesc = current ? `${current} ${transcript}` : transcript;
            updatePhoto(photoId, { description: newDesc });
        };
        rec.onerror = () => setListening(null);
        rec.onend = () => setListening(null);
        rec.start();
        recognitionRef.current = rec;
        setListening(photoId);
    };

    // ── Render ────────────────────────────────────────────────────────────
    if (loading) return <div className="p-6 text-center text-muted-foreground text-sm">Cargando...</div>;

    return (
        <div className="min-h-screen bg-background pb-24">
            {/* Header */}
            <div className="sticky top-0 z-20 bg-background border-b">
                <div className="flex items-center gap-3 px-4 py-3">
                    <Link to={`${basePath}/service/${serviceId}`}>
                        <Button variant="ghost" size="icon"><ArrowLeft className="w-5 h-5" /></Button>
                    </Link>
                    <div className="flex-1">
                        <h1 className="font-semibold text-base leading-tight">Reporte fotográfico</h1>
                        <p className="text-xs text-muted-foreground">{isAudit ? "Auditoría — Oportunidades de mejora" : "Paso 0 — Oportunidades de mejora"}</p>
                    </div>
                    <Badge variant={totalPhotos >= MAX_PHOTOS ? "destructive" : "secondary"} className="text-xs">
                        {totalPhotos}/{MAX_PHOTOS} fotos
                    </Badge>
                </div>

                {/* Zone tabs */}
                <div className="flex border-t">
                    {ZONES.map((z) => {
                        const count = zonePhotos(z.key).length;
                        return (
                            <button
                                key={z.key}
                                onClick={() => setActiveZone(z.key)}
                                data-testid={`zone-tab-${z.key}`}
                                className={`flex-1 py-2.5 text-xs font-semibold flex flex-col items-center gap-0.5 border-b-2 transition-colors ${
                                    activeZone === z.key ? "border-foreground text-foreground" : "border-transparent text-muted-foreground"
                                }`}
                            >
                                <span className="flex items-center gap-1.5">
                                    <span className={`w-2 h-2 rounded-full ${z.dot}`}></span>
                                    {z.label}
                                </span>
                                {count > 0 && <span className="text-[10px] text-muted-foreground">{count} foto{count !== 1 ? "s" : ""}</span>}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Zone content */}
            <div className="px-4 pt-4 space-y-4">
                {zonePhotos(activeZone).length === 0 && (
                    <div className="text-center py-10 text-muted-foreground text-sm">
                        <Camera className="w-8 h-8 mx-auto mb-2 opacity-30" />
                        <p>Sin fotos en esta zona</p>
                        <p className="text-xs mt-1">Toca el botón para agregar una foto</p>
                    </div>
                )}

                {zonePhotos(activeZone).map((photo) => (
                    <PhotoCard
                        key={photo.id}
                        photo={photo}
                        zone={ZONES.find((z) => z.key === photo.zone)}
                        areas={AREAS[photo.zone]}
                        isEditing={editId === photo.id}
                        onToggleEdit={() => setEditId(editId === photo.id ? null : photo.id)}
                        onUpdate={(fields) => updatePhoto(photo.id, fields)}
                        onDelete={() => handleDelete(photo.id)}
                        isListening={listening === photo.id}
                        onVoice={() => toggleVoice(photo.id)}
                        serviceId={serviceId}
                    />
                ))}
            </div>

            {/* Add photo FAB */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={handleFileSelect}
                data-testid="photo-file-input"
            />
            <div className="fixed bottom-6 left-0 right-0 px-4 flex gap-3">
                <Button
                    className="flex-1 h-12 text-sm font-semibold gap-2"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading || totalPhotos >= MAX_PHOTOS}
                    data-testid="add-photo-btn"
                >
                    {uploading ? (
                        <span className="flex items-center gap-2"><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>Subiendo...</span>
                    ) : (
                        <><Camera className="w-4 h-4" /> Agregar foto — {ZONES.find((z) => z.key === activeZone)?.label}</>
                    )}
                </Button>
                {totalPhotos > 0 && (
                    <Button
                        variant="outline"
                        className="h-12 px-5 gap-2 font-semibold text-sm"
                        onClick={() => navigate(isAudit ? `/audit/service/${serviceId}/flow?step=checklist` : `/tech/service/${serviceId}`)}
                        data-testid="save-photo-report-btn"
                    >
                        <CheckCircle2 className="w-4 h-4" />
                        {isAudit ? "Siguiente" : "Listo"}
                    </Button>
                )}
                {isAudit && totalPhotos === 0 && (
                    <Button
                        variant="outline"
                        className="h-12 px-5 gap-2 font-semibold text-sm"
                        onClick={() => navigate(`/audit/service/${serviceId}/flow?step=checklist`)}
                        data-testid="skip-photo-report-btn"
                    >
                        Omitir
                    </Button>
                )}
            </div>
        </div>
    );
}

// ── Photo card component ─────────────────────────────────────────────────────

function PhotoCard({ photo, zone, areas, isEditing, onToggleEdit, onUpdate, onDelete, isListening, onVoice, serviceId }) {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const imgSrc = photo.preview || `${API_URL}${photo.photo_url}`;

    return (
        <Card className="overflow-hidden" data-testid={`photo-card-${photo.id}`}>
            {/* Photo thumbnail row */}
            <div className="flex gap-3 p-3">
                <img
                    src={imgSrc}
                    alt="Hallazgo"
                    className="w-24 h-24 object-cover rounded border shrink-0 cursor-pointer"
                    onClick={onToggleEdit}
                />
                <div className="flex-1 min-w-0">
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded border ${zone?.color}`}>
                        <span className={`w-2 h-2 rounded-full ${zone?.dot}`}></span>
                        {zone?.label}
                    </span>
                    <p className="text-xs font-medium mt-1.5 truncate">{photo.custom_area || photo.area}</p>
                    {photo.description && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{photo.description}</p>}
                    <div className="flex items-center gap-2 mt-1.5">
                        {photo.is_reincident && <Badge variant="destructive" className="text-[10px] py-0">Reincidente</Badge>}
                        <span className="text-[10px] text-muted-foreground">Toca para editar</span>
                    </div>
                </div>
                <Button variant="ghost" size="icon" className="shrink-0 text-destructive h-8 w-8" onClick={onDelete} data-testid={`delete-photo-${photo.id}`}>
                    <Trash2 className="w-4 h-4" />
                </Button>
            </div>

            {/* Expandable edit form */}
            {isEditing && (
                <div className="border-t px-3 pb-3 pt-2 space-y-3 bg-muted/30">
                    {/* Area select */}
                    <div>
                        <Label className="text-xs">Área</Label>
                        <Select value={photo.area} onValueChange={(v) => onUpdate({ area: v })}>
                            <SelectTrigger className="h-9 text-xs mt-1" data-testid={`area-select-${photo.id}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {areas.map((a) => <SelectItem key={a} value={a} className="text-xs">{a}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Custom area input if "Otra" */}
                    {photo.area === "Otra" && (
                        <div>
                            <Label className="text-xs">Especificar área</Label>
                            <input
                                className="mt-1 w-full h-9 rounded-md border bg-background px-3 text-xs"
                                value={photo.custom_area || ""}
                                onChange={(e) => onUpdate({ custom_area: e.target.value })}
                                placeholder="Ej: Baño de empleados"
                                data-testid={`custom-area-${photo.id}`}
                            />
                        </div>
                    )}

                    {/* Description + voice */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <Label className="text-xs">Descripción del hallazgo</Label>
                            <Button
                                variant="ghost" size="icon" className={`h-7 w-7 ${isListening ? "text-red-500" : "text-muted-foreground"}`}
                                onClick={onVoice} data-testid={`voice-btn-${photo.id}`}
                            >
                                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                            </Button>
                        </div>
                        <Textarea
                            rows={2}
                            className="text-xs resize-none"
                            value={photo.description || ""}
                            onChange={(e) => onUpdate({ description: e.target.value })}
                            placeholder="Describa brevemente el hallazgo..."
                            data-testid={`description-${photo.id}`}
                        />
                    </div>

                    {/* Reincident switch */}
                    <div className="flex items-center justify-between rounded-md border px-3 py-2">
                        <Label htmlFor={`sw-rein-${photo.id}`} className="text-xs cursor-pointer">¿Es reincidente?</Label>
                        <Switch
                            id={`sw-rein-${photo.id}`}
                            checked={!!photo.is_reincident}
                            onCheckedChange={(v) => onUpdate({ is_reincident: v })}
                            data-testid={`reincident-switch-${photo.id}`}
                        />
                    </div>
                </div>
            )}
        </Card>
    );
}
